import { Injectable } from '@angular/core';
import { NavigationEnd, Router, Event, RouterEvent, ActivatedRoute, Scroll } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';

import { SocialRouteModel } from '../../SocialRouter/models/social-route.model';
import { SocialRouteTypeEnum } from '../../SocialRouter/enums/social-route-type.enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';


@Injectable()
export class SocialRoutingService {

    // Breaks the URL into known pieces so the current location can be evaluated
    // eslint-disable-next-line max-len
    private readonly urlParse: RegExp = /Social\/?(themes|archive)?\/?(theme|agent|results)?\/?([a-zA-Z0-9]+)?\/?(agent|dashboard|postFeed|analytics)?\/?(\d+)?/;
    private readonly socialRootPath: string = 'Social';
    private currentRoute$: Observable<SocialRouteModel> = this.router.events.pipe(
        filter((x: Event | RouterEvent) => {

            if (x instanceof Scroll) {
                return x.routerEvent instanceof NavigationEnd;
            }
            return x instanceof NavigationEnd;
        }),
        map<RouterEvent, SocialRouteModel>(
            routerEvent => {
                if (routerEvent instanceof Scroll) {
                    return this.getRouteModel(routerEvent.routerEvent.url);
                }
                return this.getRouteModel(routerEvent.url);
            }
        ),
        // If the routeModel couldn't be generated, don't emit
        filter(x => !!x),
        shareReplay(1)
    );

    // eslint-disable-next-line rxjs/no-exposed-subjects
    isArchiveSearch$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private titleService: Title,
        private localizationService: LocalizationService
    ) {}

    public getCurrentRoute$(): Observable<SocialRouteModel> {
        return this.currentRoute$;
    }

    /**
     * Returns a route model based on a url passed in
     *
     * @param url - url to base route model on
     *
     * @returns SocialRouteModel
     */
    public getRouteModel(url: string): SocialRouteModel {
        const matches = url.match(this.urlParse);
        let route: SocialRouteModel;
        if (!matches) {
            return;
        } else {
            switch (matches[1]) {
                // Social/themes
                case 'themes':
                    // Social/themes/theme
                    if (matches[2] === 'theme') {
                        // Social/themes/theme/{theme_id}/postFeed
                        if (matches[4] === 'postFeed') {
                            route = new SocialRouteModel(
                                SocialRouteTypeEnum.postFeed,
                                +matches[3]
                            );
                        // Social/themes/theme/{theme_id}/analytics
                        } else if (matches[4] === 'analytics') {
                            route = new SocialRouteModel(
                                SocialRouteTypeEnum.analytics,
                                +matches[3]
                            );
                        } else {
                            // Fallback - Social/themes/theme/{theme_id}/analytics
                            route = new SocialRouteModel(
                                SocialRouteTypeEnum.analytics,
                                +matches[3]
                            );
                        }
                    } else {
                        // Fallback - Social/themes/*
                        route = new SocialRouteModel(SocialRouteTypeEnum.themeManagement);
                    }
                    break;
                case 'archive':
                    // Social/archive
                    route = new SocialRouteModel(SocialRouteTypeEnum.archive);
                    if (matches[2] === 'results') {
                        if (matches[3] === 'analytics') {
                            route = new SocialRouteModel(SocialRouteTypeEnum.archiveAnalytics);
                        } else if (matches[3] === 'postFeed') {
                            route = new SocialRouteModel(SocialRouteTypeEnum.archivePostFeed);
                        }
                    }
                    break;
                default:
                    // Fallback - Social/*
                    route = new SocialRouteModel(SocialRouteTypeEnum.themeManagement);
            }
        }
        this.isArchiveSearch(route);
        this.setBrowserTitle();
        return route;
    }

    private setBrowserTitle() {
        let child = this.route.firstChild;
        while (child.firstChild) {
            child = child.firstChild;
        }
        if (!!child.snapshot.data['title']) {
            this.titleService.setTitle(
                this.localizationService.transform(child.snapshot.data['title'])
            );
        }
    }

    public gotoRootPath(){
        this.router.navigate([this.socialRootPath]);
    }

    isArchiveSearch(route: SocialRouteModel): void {
        this.isArchiveSearch$.next(
            route?.type === SocialRouteTypeEnum.archive
            || route?.type === SocialRouteTypeEnum.archiveAnalytics
            || route?.type === SocialRouteTypeEnum.archivePostFeed
        );
    }
}
